import axios, { type AxiosInstance, type CancelTokenSource } from 'axios'
import type { IAuthService } from './auth-service'
import type {
  UserDetail,
  Application,
  Region,
  Country,
  PagedResponse,
  UserFilters,
  SolClient,
  UserRolesUpdateRequest,
} from '@/types/domain'

export interface INotificationService {
  error(title: string, message: string): void
}

export interface IApiService {
  getApplications(): Promise<{ content: Application[] }>
  getRegions(): Promise<Region[]>
  getCountries(region: string): Promise<PagedResponse<Country>>
  getLoggedUserDetail(): Promise<UserDetail>
  getUsers(filters: UserFilters, cancelKey?: string): Promise<PagedResponse<UserDetail>>
  getUserDetail(userId: string): Promise<UserDetail>
  checkUserExist(userId: string): Promise<boolean>
  getProUser(internalId: string): Promise<UserDetail | null>
  createUser(user: Partial<UserDetail>): Promise<UserDetail>
  updateUser(user: Partial<UserDetail>, id: string): Promise<UserDetail>
  updateUserRoles(userId: string, apps: UserRolesUpdateRequest): Promise<UserDetail>
  changeStatusUser(userId: string, active: boolean): Promise<UserDetail>
  resendWelcomeEmail(userId: string): Promise<void>
  getSolClient(countryCode: string, clientCode: string, sourceSystem: string): Promise<SolClient | null>
  generateReportUsers(filters: UserFilters): Promise<Blob | null>
  downloadBulkUserTemplate(): Promise<Blob | null>
  bulkUserUpload(file: FormData): Promise<unknown>
}

export default class ApiService implements IApiService {
  private readonly http: AxiosInstance
  private readonly authService: IAuthService
  private readonly cancelSources: Record<string, CancelTokenSource> = {}
  private loadingCount = 0
  private notificationService: INotificationService | null = null

  constructor(authService: IAuthService) {
    this.authService = authService
    this.http = axios.create({
      baseURL: `${import.meta.env.VITE_SVC_BACKEND_BASE_URL}/api`,
      headers: { 'Cache-Control': 'no-cache' },
    })

    this.http.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.headers['www-authenticate'] && (await this.authService.getIdToken())) {
          await this.authService.signOut()
        } else if (error.response === undefined && !error.__CANCEL__) {
          this.notificationService?.error('networkError.title', 'networkError.description')
        }
        throw error
      },
    )
  }

  setNotificationService(ns: INotificationService): void {
    this.notificationService = ns
  }

  private async startLoading(): Promise<void> {
    this.loadingCount++
    if (this.loadingCount === 1) {
      const { useAppStore } = await import('@/stores/app')
      useAppStore().startLoading()
    }
  }

  private async stopLoading(): Promise<void> {
    this.loadingCount = Math.max(0, this.loadingCount - 1)
    if (this.loadingCount === 0) {
      const { useAppStore } = await import('@/stores/app')
      useAppStore().stopLoading()
    }
  }

  private handleError(error: unknown): Promise<never> {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response
      if (status === 400 && data?.message) {
        let message: string
        try {
          ;({ message } = JSON.parse(data.message))
        } catch {
          message = data.message
        }
        this.notificationService?.error('global.error', message)
      } else {
        this.notificationService?.error('error500.title', 'error500.description')
      }
    }
    return Promise.reject(error)
  }

  private async setAuthHeader(): Promise<void> {
    const idToken = await this.authService.getIdToken()
    if (idToken) {
      this.http.defaults.headers.common['Authorization'] = `Bearer ${idToken}`
    }
  }

  private cancelPrevious(key: string): CancelTokenSource {
    if (this.cancelSources[key]) {
      this.cancelSources[key].cancel()
    }
    const source = axios.CancelToken.source()
    this.cancelSources[key] = source
    return source
  }

  async getApplications(): Promise<{ content: Application[] }> {
    await this.setAuthHeader()
    const { data } = await this.http.get<{ content: Application[] }>('/applications')
    return data
  }

  async getRegions(): Promise<Region[]> {
    await this.setAuthHeader()
    const { data } = await this.http.get<Region[]>('/region')
    return data
  }

  async getCountries(region: string): Promise<PagedResponse<Country>> {
    await this.setAuthHeader()
    const { data } = await this.http.get<PagedResponse<Country>>('/country', {
      params: { regionName: region, active: true },
    })
    return data
  }

  async getLoggedUserDetail(): Promise<UserDetail> {
    await this.setAuthHeader()
    const { data } = await this.http.get<UserDetail>('/users/me')
    return data
  }

  async getUsers(filters: UserFilters, cancelKey = ''): Promise<PagedResponse<UserDetail>> {
    const source = this.cancelPrevious(`/users${cancelKey}`)
    await this.setAuthHeader()
    const { data } = await this.http.get<PagedResponse<UserDetail>>('/users', {
      params: filters,
      cancelToken: source.token,
    })
    delete this.cancelSources[`/users${cancelKey}`]
    return data
  }

  async getUserDetail(userId: string): Promise<UserDetail> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.get<UserDetail>(`/users/${userId}`)
      return data
    } finally {
      await this.stopLoading()
    }
  }

  async checkUserExist(userId: string): Promise<boolean> {
    await this.setAuthHeader()
    try {
      const { data } = await this.http.get<boolean>(`/users/available/${userId}`)
      return data ?? false
    } catch {
      return true
    }
  }

  async getProUser(internalId: string): Promise<UserDetail | null> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.get<UserDetail>(`/api-global/${internalId}`)
      return data
    } finally {
      await this.stopLoading()
    }
  }

  async createUser(user: Partial<UserDetail>): Promise<UserDetail> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.post<UserDetail>('/users', user)
      return data
    } catch (error) {
      return this.handleError(error)
    } finally {
      await this.stopLoading()
    }
  }

  async updateUser(user: Partial<UserDetail>, id: string): Promise<UserDetail> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.put<UserDetail>(`/users/${id}`, user)
      return data
    } catch (error) {
      return this.handleError(error)
    } finally {
      await this.stopLoading()
    }
  }

  async updateUserRoles(userId: string, apps: UserRolesUpdateRequest): Promise<UserDetail> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.put<UserDetail>(`/users/${userId}/role`, apps)
      return data
    } catch (error) {
      return this.handleError(error)
    } finally {
      await this.stopLoading()
    }
  }

  async changeStatusUser(userId: string, active: boolean): Promise<UserDetail> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.patch<UserDetail>(`/users/${userId}/status`, { active })
      return data
    } finally {
      await this.stopLoading()
    }
  }

  async resendWelcomeEmail(userId: string): Promise<void> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      await this.http.post(`/users/${userId}/resend-welcome-email`)
    } finally {
      await this.stopLoading()
    }
  }

  async getSolClient(
    countryCode: string,
    clientCode: string,
    sourceSystem: string,
  ): Promise<SolClient> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.get<SolClient>(`/entities/${countryCode}/${clientCode}`, {
        params: { sourceSystem },
      })
      return data
    } finally {
      await this.stopLoading()
    }
  }

  async generateReportUsers(filters: UserFilters): Promise<Blob | null> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.get<Blob>('/report/users', {
        responseType: 'blob',
        params: filters,
      })
      return data
    } catch (error) {
      return this.handleError(error)
    } finally {
      await this.stopLoading()
    }
  }

  async downloadBulkUserTemplate(): Promise<Blob | null> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.get<Blob>('/bulk/users/template', {
        responseType: 'blob',
        headers: { 'Content-Type': 'blob' },
      })
      return data
    } catch (error) {
      return this.handleError(error)
    } finally {
      await this.stopLoading()
    }
  }

  async bulkUserUpload(file: FormData): Promise<unknown> {
    await this.startLoading()
    try {
      await this.setAuthHeader()
      const { data } = await this.http.post<unknown>('/bulk/users', file, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data
    } catch (error) {
      return this.handleError(error)
    } finally {
      await this.stopLoading()
    }
  }
}
